/**
 * DigX Reviews widget -- progressive-enhancement layer (Task 13, redesigned
 * 2026-08-27 per CONTROL-SPEC.md section B / defect 3).
 *
 * The widget is FULLY readable and correctly ordered with this script
 * disabled: every review is server-rendered from the product's
 * digx_reviews.snapshot metafield (snippets/digx-reviews-widget.liquid),
 * already in newest-first order (matching the sort <select>'s default
 * "recent" option). Filtering is the only thing this widget cannot do with
 * JS off: the breakdown chart still renders every bar and every count, it
 * just does not narrow the list until this file binds it. This file adds:
 *
 *   1. Sort -- reorders the already-rendered <li> elements by
 *      data-rating/data-date. No fetch, no re-render: every review a
 *      shopper can see after sorting was already in the served HTML.
 *   2. Filter -- clicking a star-rating bar in the breakdown chart hides
 *      every <li> whose data-rating doesn't match (client-side only; all
 *      <=10 reviews the snapshot ever carries are already in the DOM, so
 *      there is nothing to fetch).
 *   3. Helpful vote -- a per-browser toggle persisted to localStorage.
 *      IMPORTANT: this is NOT a shared vote count. The snapshot has no
 *      helpful-vote field (lib/reviews/snapshot.ts's SnapReview carries
 *      id/rating/title/body/author/date/photos/video only), so there is no
 *      backend to aggregate a real cross-visitor count. Counts start at 0
 *      (the server-rendered data-base-count) and increment by exactly 1,
 *      locally, when the viewer's own browser presses the button --
 *      visible only to that browser, never claimed as a shared total.
 *   4. Load more (Task 16, 2026-08-27) -- the snapshot metafield the
 *      product page renders from is capped at 10 reviews
 *      (lib/reviews/snapshot.ts's DEFAULT_MAX_REVIEWS) even when a product
 *      has far more published, so a shopper could never reach review 11+ --
 *      a functional regression versus the Judge.me widget being replaced,
 *      which paginates through every review. The button (rendered
 *      server-side only when snap.count exceeds what the snapshot carries,
 *      see the Liquid snippet's own comment) fetches additional pages from
 *      the App Proxy's list endpoint with `variant=product` -- the ONE
 *      backend option that makes that endpoint emit the exact same
 *      `<li class="digx-review">...` shape this file's own server-rendered
 *      markup already uses -- and appends the returned `<li>` elements
 *      straight into the list, so there is no visible seam at the append
 *      point. See initLoadMore() below for the fetch/append/re-sort
 *      sequence and its own error handling.
 *
 * PASS 4 (2026-08-28) added three things, all of them small:
 *
 *   5. THE APPENDED ROWS ARE NORMALISED TO THE SHOP'S OWN SETTINGS. The
 *      load-more endpoint lives in a different repo and cannot see a theme
 *      setting, so it always returns a review with its media block and its
 *      helpful-vote row. With "show customer photos" or "show the helpful
 *      buttons" switched off, page 2 would therefore contradict page 1 on
 *      the same screen. The resolved flags ride on the widget root as
 *      data-show-media / data-show-votes (written by
 *      snippets/digx-reviews-widget.liquid) and normalizeAppendedItem()
 *      applies them BEFORE the <li> enters the list, so a shopper never
 *      sees the unstyled frame of a block that is about to disappear.
 *      The lightbox flag needs no per-row work and that is deliberate: the
 *      photo anchor's two lightbox attributes are emitted on BOTH sides
 *      unconditionally and are inert without the overlay shell, so an
 *      appended row and a server-rendered row already agree.
 *
 *   6. AN APPLIED FILTER IS VISIBLE ON THE FILTER BUTTON. Picking a rating
 *      closes the panel, so before this the only evidence a filter was on
 *      was a shorter list -- which reads as "this product has few reviews".
 *
 *   7. THE ERROR MESSAGE LOOKS DIFFERENT FROM THE LOADING MESSAGE. One
 *      <div> carries both, and they used to render identically.
 */
(function () {
  "use strict";

  var LOAD_MORE_ENDPOINT = "/apps/reviews";

  /**
   * Sorts the CURRENT list children (queried fresh, not cached -- so
   * load-more-appended items are included every time this runs) by the
   * given sort value and re-appends them in that order. Shared by the sort
   * <select>'s own "change" handler and initLoadMore()'s re-apply step, so
   * a shopper who picked a sort and then loaded more never sees the
   * newly-appended reviews sitting out of order at the bottom.
   */
  function sortListItems(list, value) {
    var items = Array.prototype.slice.call(list.children);
    items.sort(function (a, b) {
      if (value === "highest" || value === "lowest") {
        var ra = parseInt(a.getAttribute("data-rating"), 10) || 0;
        var rb = parseInt(b.getAttribute("data-rating"), 10) || 0;
        return value === "highest" ? rb - ra : ra - rb;
      }
      // "recent" (default): ISO YYYY-MM-DD dates compare correctly as
      // plain strings.
      var da = a.getAttribute("data-date") || "";
      var db = b.getAttribute("data-date") || "";
      if (da === db) return 0;
      return da > db ? -1 : 1;
    });
    items.forEach(function (li) {
      list.appendChild(li);
    });
  }

  function initSort(root, list) {
    var select = root.querySelector("[data-digx-sort]");
    if (!select) return null;

    select.addEventListener("change", function () {
      sortListItems(list, select.value);
    });

    return select;
  }

  /**
   * Hides/shows every CURRENT list child (queried fresh, same reason as
   * sortListItems above) against the given active rating ("" = show all).
   * Shared by the filter panel's own row click handler and initLoadMore()'s
   * re-apply step, so a shopper who filtered to (e.g.) 5 stars and then
   * loaded more doesn't see un-filtered reviews appear at the bottom.
   */
  function applyFilterToList(list, active) {
    Array.prototype.slice.call(list.children).forEach(function (li) {
      var show = active === "" || li.getAttribute("data-rating") === active;
      li.hidden = !show;
    });
  }

  /**
   * THE DISTRIBUTION CHART IS THE FILTER -- 2026-08-30.
   *
   * This used to bind rows inside a <details> panel that hung off a funnel
   * button. Nikita: "Filters button does not work, and what are you
   * filtering? People should be able to click on the fucking review bar and
   * once it is clicked that particular filter is applied." Both complaints
   * were real. The panel measured 34px wide on the live page, so its rows
   * were an unreadable sliver, and its five rows were a second copy of the
   * chart already drawn above it.
   *
   * The panel is gone. `[data-digx-filter-panel]` now sits on the chart's own
   * <ul>, and the rows are the bars. Everything below is the same mechanism
   * as before -- toggle, re-press to clear, re-apply after load-more -- minus
   * the open/close bookkeeping a disclosure needed.
   */
  function initFilter(root, list) {
    var panel = root.querySelector("[data-digx-filter-panel]");
    if (!panel) return null;

    var rows = Array.prototype.slice.call(panel.querySelectorAll("button[data-digx-filter-rating]"));
    // Nothing to bind when the shop switched filtering off: the Liquid then
    // renders the same chart as plain <div> rows, so there is no button here
    // and no dead control either.
    if (rows.length === 0) return null;

    var clearBtn = root.querySelector("[data-digx-filter-clear]");
    var state = { active: "" };

    function render() {
      applyFilterToList(list, state.active);
      rows.forEach(function (r) {
        r.setAttribute(
          "aria-pressed",
          r.getAttribute("data-digx-filter-rating") === state.active ? "true" : "false"
        );
      });
      // The way back. Pressing the active bar again also clears the filter,
      // but a shopper has no way to know that, so the reset is spelled out
      // whenever there is something to reset.
      if (clearBtn) clearBtn.hidden = state.active === "";
    }

    function setActive(rating) {
      state.active = rating === state.active ? "" : rating;
      render();
    }

    rows.forEach(function (row) {
      row.addEventListener("click", function () {
        if (row.disabled) return;
        setActive(row.getAttribute("data-digx-filter-rating"));
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener("click", function () {
        state.active = "";
        render();
      });
    }

    return state;
  }

  function initVoting(root) {
    var STORAGE_KEY = "digx-reviews-votes-v1";

    function readVotes() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        return {};
      }
    }

    function writeVotes(votes) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(votes));
      } catch (e) {
        /* localStorage unavailable (private mode, quota) -- vote state
           just won't persist across reloads; the click still gives visual
           feedback for the current page view. */
      }
    }

    var votes = readVotes();

    function render(reviewId, state) {
      Array.prototype.slice
        .call(root.querySelectorAll('[data-digx-vote][data-review-id="' + cssEscape(reviewId) + '"]'))
        .forEach(function (b) {
          var direction = b.getAttribute("data-digx-vote");
          var countEl = b.querySelector("[data-digx-vote-count]");
          var base = parseInt(countEl.getAttribute("data-base-count"), 10) || 0;
          var isActive = state === direction;
          b.setAttribute("aria-pressed", isActive ? "true" : "false");
          b.classList.toggle("is-active", isActive);
          countEl.textContent = String(base + (isActive ? 1 : 0));
        });
    }

    function cssEscape(value) {
      // Minimal CSS.escape stand-in (review ids are server-generated UUIDs,
      // never untrusted input, but this keeps the attribute selector above
      // safe even so): escape only the characters that could break a
      // ["..."] attribute selector.
      return String(value).replace(/["\\]/g, "\\$&");
    }

    function wireButton(btn) {
      var reviewId = btn.getAttribute("data-review-id");
      render(reviewId, votes[reviewId]);

      // reviewId is read FRESH off the button on every click, 2026-08-29 --
      // NOT the `reviewId` closed over above. Every server-rendered row
      // button's id is fixed for its whole lifetime, so this is a no-op
      // change for them. It is load-bearing for the lightbox overlay's own
      // two vote buttons (assets/digx-reviews-lightbox.js's fillInfo()):
      // those are wired ONCE, at page load, with whatever data-review-id
      // they happened to carry then (none, on first paint), and get a NEW
      // id written onto the same DOM node every time a different review's
      // photo opens. Capturing reviewId in this closure would keep every
      // click routed to whichever review was on screen when init() ran.
      btn.addEventListener("click", function () {
        var currentReviewId = btn.getAttribute("data-review-id");
        var direction = btn.getAttribute("data-digx-vote");
        var current = votes[currentReviewId];
        var next = current === direction ? null : direction;
        if (next) {
          votes[currentReviewId] = next;
        } else {
          delete votes[currentReviewId];
        }
        writeVotes(votes);
        // render() re-queries the WHOLE widget root for every button that
        // shares this review id, so a press on the overlay's button and a
        // press on the row's button both land on the same pair of buttons.
        render(currentReviewId, votes[currentReviewId]);
      });
    }

    // Delegated + a live re-scan on load-more append (see initLoadMore)
    // would both work; wiring each button once, up front AND once more
    // after every load-more append (initLoadMore calls wireNewVoteButtons
    // below), keeps this function's own logic identical to the pre-Task-16
    // version for the buttons that were already server-rendered on load.
    Array.prototype.slice.call(root.querySelectorAll("[data-digx-vote]")).forEach(wireButton);

    return { wireButton: wireButton };
  }

  /**
   * Parses a load-more response body (the same `<li>...</li><template
   * class="digx-reviews__meta" ...></template>` shape
   * renderReviewListFragment always returns) into its `<li>` elements plus
   * the trailing meta template, WITHOUT executing any script or image
   * request the markup might contain -- a <template> element's content is
   * inert until explicitly adopted, exactly what's needed to safely parse
   * server HTML on the client.
   */
  function parseListFragment(html) {
    var holder = document.createElement("template");
    holder.innerHTML = html;
    var items = [];
    var meta = null;
    Array.prototype.forEach.call(holder.content.children, function (node) {
      if (node.tagName === "LI") {
        items.push(node);
      } else if (node.tagName === "TEMPLATE" && node.classList.contains("digx-reviews__meta")) {
        meta = node;
      }
    });
    return { items: items, meta: meta };
  }

  /**
   * Wires the "load more" button (Task 16): fetches the next page in the
   * PRODUCT item shape (variant=product), appends its <li>s to the list,
   * re-applies whatever sort/filter the shopper currently has active so the
   * appended reviews land in the right place instead of just tacking onto
   * the bottom, advances the page counter, and hides the button once the
   * server reports no more pages. A failed fetch restores the button (never
   * leaves it stuck in a loading state) with an inline error message the
   * shopper can retry from.
   */
  /**
   * A stable identity for one rendered review, used only by the load-more
   * overlap guard.
   *
   * The review id lives on the vote buttons and NOWHERE else in the row --
   * the <li> itself carries only rating and date, and that markup is frozen
   * by the byte-parity contract with lib/reviews/fragments.ts. So the
   * moment a shop owner switches the helpful-vote row off, the id is not in
   * the markup at all and the guard would have had nothing to compare.
   * The fallback is a content key that every render carries: rating, date,
   * and the first 80 characters of the body. Two DIFFERENT reviews
   * colliding on all three is not a case worth engineering against; the
   * same review appearing twice is exactly what this guard exists to stop.
   *
   * CALL IT ON A NORMALISED NODE, ALWAYS. Which of the two keys comes back
   * depends on whether the row has a vote button, so an un-normalised
   * fragment and a page that renders no vote rows land in different key
   * spaces and nothing ever matches. See the append loop in initLoadMore.
   */
  function reviewKeyOf(li) {
    if (!li || typeof li.querySelector !== "function") return null;

    var voteButton = li.querySelector("[data-review-id]");
    if (voteButton) return "id:" + voteButton.getAttribute("data-review-id");

    var body = li.querySelector(".digx-review__body");
    return [
      "row",
      li.getAttribute("data-rating") || "",
      li.getAttribute("data-date") || "",
      body ? body.textContent.slice(0, 80) : ""
    ].join("|");
  }

  /**
   * The resolved shop settings, read once off the widget root. A missing
   * attribute reads as ON: that is the default every one of these settings
   * ships with, and it keeps an older cached page rendering the way it
   * always did rather than silently stripping blocks out of it.
   */
  function readFlags(root) {
    return {
      showMedia: root.getAttribute("data-show-media") !== "false",
      showVotes: root.getAttribute("data-show-votes") !== "false"
    };
  }

  /**
   * Brings one server-rendered <li> into line with this shop's settings
   * before it is appended. The endpoint that produced it lives in a
   * different repo and cannot see a theme setting, so it always sends the
   * media block and the vote row.
   *
   * REMOVED, not hidden: a hidden block is still in the accessibility tree
   * and still a tab stop for the photo anchors inside it, so hiding it
   * would leave page 2 reachable by keyboard while page 1 was not.
   * Called BEFORE the node is in the document, so nothing ever paints.
   */
  function normalizeAppendedItem(li, flags) {
    var drop = [];
    if (!flags.showMedia) drop.push(".digx-review__media");
    if (!flags.showVotes) drop.push(".digx-review__useful");
    if (drop.length === 0) return;

    drop.forEach(function (selector) {
      Array.prototype.slice.call(li.querySelectorAll(selector)).forEach(function (node) {
        if (node.parentNode) node.parentNode.removeChild(node);
      });
    });
  }

  function initLoadMore(root, list, sortState, helpers, flags) {
    var button = root.querySelector("[data-digx-load-more]");
    if (!button) return;

    var status = root.querySelector("[data-digx-load-more-status]");
    var productId = button.getAttribute("data-product-id");
    if (!productId) return; // nothing to fetch without a product id -- stay hidden, no dead click

    // No-JS safety: the button ships `hidden` from the server (see the
    // Liquid snippet's own comment) and is revealed ONLY here, once a real
    // click handler is about to be wired below -- so a visitor with JS
    // disabled never sees a control that can't do anything.
    button.hidden = false;

    var nextPage = parseInt(button.getAttribute("data-next-page"), 10) || 2;

    // Every review id already rendered, so an overlapping page cannot double
    // up. The id lives on the vote buttons (the <li> itself carries only
    // rating/date), which both the Liquid and the TS renderer emit.
    var seenIds = {};
    Array.prototype.slice.call(list.children).forEach(function (li) {
      var key = reviewKeyOf(li);
      if (key) seenIds[key] = true;
    });
    // Loading/error copy comes from data attributes the Liquid snippet
    // populates via `| t` (locales/*.json's digx_reviews.load_more_*
    // keys) -- this script never hardcodes user-visible copy in any
    // language, same rule the rest of the widget follows.
    var loadingLabel = button.getAttribute("data-loading-label") || "";
    var errorLabel = button.getAttribute("data-error-label") || "";

    /* One <div> carries the loading line and the error line, so it also
       carries which of the two it is -- they rendered identically before,
       and "Cargando reseñas..." and "no se han podido cargar" mean opposite
       things. assets/digx-reviews.css styles .is-error. */
    function setStatus(message, isError) {
      if (!status) return;
      status.textContent = message || "";
      status.classList.toggle("is-error", !!isError);
    }

    function setLoading(isLoading) {
      button.disabled = isLoading;
      button.classList.toggle("is-loading", isLoading);
      setStatus(isLoading ? loadingLabel : "", false);
    }

    button.addEventListener("click", function () {
      setLoading(true);

      var url =
        LOAD_MORE_ENDPOINT +
        "?action=list&product_id=" +
        encodeURIComponent(productId) +
        "&page=" +
        encodeURIComponent(String(nextPage)) +
        // variant=product: byte-parity with this file's own server-rendered
        // <li> shape (lib/reviews/fragments.ts's reviewProductItemHtml).
        // sort=newest, ALWAYS, regardless of the shopper's own sort pick:
        // the snapshot metafield this list started from is unconditionally
        // newest-first (loadSnapshotSource orders by desc(publishedAt) with
        // no configurable sort at all), so continuing the SAME underlying
        // order is the only way page 2+ can avoid re-showing or skipping a
        // review relative to what's already on the page. The shopper's own
        // sort/filter picks are re-applied client-side, below, after the
        // new items land.
        "&variant=product&sort=newest";

      fetch(url, { headers: { Accept: "text/html" } })
        .then(function (response) {
          if (!response.ok) throw new Error("digx-reviews load-more: bad status " + response.status);
          return response.text();
        })
        .then(function (html) {
          var parsed = parseListFragment(html);
          // Overlap guard (2026-08-27). The first page on screen came from
          // the SNAPSHOT metafield, whose size is its own cap (10 reviews, or
          // fewer when the 60KB byte cap bites), while the list endpoint pages
          // by the store's reviewsPerPage setting. Those two numbers agree
          // today (both 10) but nothing enforces it, so a settings change or a
          // byte-capped snapshot would silently re-append reviews already on
          // the page. Skipping ids the list already carries makes any overlap
          // harmless instead of visible.
          parsed.items.forEach(function (li) {
            // NORMALISE FIRST, KEY SECOND, and that order is the whole
            // point. reviewKeyOf falls back to a content key when the row
            // carries no review id, and the vote row is the only node that
            // carries one -- so keying the raw fragment while the rows
            // already on the page had been rendered WITHOUT a vote row put
            // the two sides in different key spaces and the guard never
            // matched. Caught by a real load-more press with the helpful
            // buttons switched off: page 2 re-appended a review page 1 was
            // already showing. Normalising first means both sides are in
            // whatever key space this shop's own settings produce.
            // It also means the settings are applied while the node is
            // still detached, so a block the shop owner turned off never
            // paints for a frame.
            normalizeAppendedItem(li, flags);
            var key = reviewKeyOf(li);
            if (key && seenIds[key]) return;
            if (key) seenIds[key] = true;
            list.appendChild(li);
            // Newly-appended vote buttons need the same per-browser
            // localStorage wiring the initially-rendered ones got.
            Array.prototype.forEach.call(li.querySelectorAll("[data-digx-vote]"), function (btn) {
              helpers.voting.wireButton(btn);
            });
          });

          // Re-apply the shopper's current sort/filter to the now-larger
          // list so the appended reviews don't look out of place.
          if (sortState) sortListItems(list, sortState.value);
          if (helpers.filter) applyFilterToList(list, helpers.filter.active);

          var hasMore = parsed.meta ? parsed.meta.getAttribute("data-has-more") === "true" : false;
          nextPage += 1;
          button.setAttribute("data-next-page", String(nextPage));

          if (!hasMore) {
            button.hidden = true;
          }
          setLoading(false);
        })
        .catch(function () {
          setLoading(false);
          button.hidden = false;
          setStatus(errorLabel, true);
        });
    });
  }

  function init() {
    var root = document.querySelector("[data-digx-reviews]");
    if (!root) return; // no widget on this page -- no-op, as required

    var list = root.querySelector("[data-digx-reviews-list]");
    if (!list) return;

    var flags = readFlags(root);
    var sortSelect = initSort(root, list);
    var filterState = initFilter(root, list);
    var voting = initVoting(root);

    initLoadMore(root, list, sortSelect, { filter: filterState, voting: voting }, flags);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
